import React, { useContext, useEffect, useState, useRef } from "react";
import { Modal } from "@mui/material";
import { UserContext } from "../../../../../../../../App";
import useGetDevice from "../../../../../hooks/useGetDevice";
import { memo } from "react";
import { getFromLocalStorage } from "../../../../../../../../utils/localStorageHelpers";
import { getStoreCurrencySymbol } from "../../../../../../../../utils/currency";
import "./index.css";

/* Open batch popups, newest last — only the newest handles keyboard input */
const openPopupStack = [];

const BatchSelectionModal = (props) => {
  let { color } = useContext(UserContext);
  const isMobile = useGetDevice();
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const cartId = localStorage.getItem("CartId");
  const [selectedProductIndex, setSelectedProductIndex] = useState(-1);
  const [offerProducts, setOfferProducts] = useState([]);
  const [nonOfferProducts, setNonOfferProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const showExpiry = !!getFromLocalStorage("appConfig")?.enabledFeatures?.MULTIPLE_BATCH_EXPIRY;

  /* Latest values for the keyboard listener, so it is bound once per open */
  const keyStateRef = useRef({});
  keyStateRef.current = { batches, isAdding, onClose: props.onClose };

  /* Pre-select the first batch on open so one key press = one move, and Enter adds right away */
  useEffect(() => {
    setSelectedProductIndex(props.open && batches?.length > 0 ? 0 : -1);
    if (!props.open) setIsAdding(false);
  }, [props.open, batches?.length]);

  useEffect(() => {
    const offerProds = props.mrps?.filter((mrp) => mrp?.offers !== null);
    const nonOfferProds = props.mrps?.filter((mrp) => mrp?.offers === null);
    setOfferProducts(offerProds);
    setNonOfferProducts(nonOfferProds);
  }, [props.mrps]);

  useEffect(() => {
    setBatches([...nonOfferProducts, ...offerProducts]);
  }, [nonOfferProducts, offerProducts]);

  useEffect(() => {
    if (!props.open) return;
    const popupId = {};
    openPopupStack.push(popupId);

    const handleKeyDown = (event) => {
      if (!["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(event.key)) return;
      /* Only the top-most open batch popup reacts to a key press */
      if (openPopupStack[openPopupStack.length - 1] !== popupId) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      const { batches: list, isAdding: adding, onClose } = keyStateRef.current;
      /* Ignore keyboard interaction while the item is being added to cart */
      if (adding) return;
      const count = list?.length || 0;

      if (event.key === "ArrowDown" && count) {
        setSelectedProductIndex((prev) => (prev >= count - 1 ? 0 : prev + 1));
      } else if (event.key === "ArrowUp" && count) {
        setSelectedProductIndex((prev) => (prev <= 0 ? count - 1 : prev - 1));
      } else if (event.key === "Enter") {
        buttonRef.current?.click();
      } else if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      const idx = openPopupStack.indexOf(popupId);
      if (idx !== -1) openPopupStack.splice(idx, 1);
    };
  }, [props.open]);

  /* Scroll the selected card into view when navigating with keyboard */
  useEffect(() => {
    if (selectedProductIndex >= 0 && listRef.current) {
      const card = listRef.current.children[selectedProductIndex];
      card?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedProductIndex]);

  useEffect(() => {
    props.handlePrice(
      batches?.[selectedProductIndex]?.mrp,
      batches?.[selectedProductIndex]?.sellingPrice,
      batches?.[selectedProductIndex]?.batchNum || batches?.[selectedProductIndex]?.batchNo,
      batches?.[selectedProductIndex]?.purchasePrice,
      batches?.[selectedProductIndex]?.sellingUnit,
      batches?.[selectedProductIndex]?.offers,
      batches?.[selectedProductIndex]?.availableUnits,
      batches?.[selectedProductIndex]?.expiryDate
    );
  }, [selectedProductIndex, batches]);

  const handleBatchPopupClick = (mrp, index) => {
    setSelectedProductIndex(index);
    props.handlePrice(
      mrp?.mrp,
      mrp?.sellingPrice,
      mrp?.batchNum || mrp?.batchNo,
      mrp?.purchasePrice,
      mrp?.sellingUnit,
      mrp?.offers,
      mrp?.availableUnits,
      mrp?.expiryDate
    );
  };

  const handleClose = () => {
    if (isAdding) return;
    props.onClose();
  };

  const handleAddClick = async () => {
    if (isAdding) return;
    if (props.onBatchConfirm) {
      props.onBatchConfirm(batches[selectedProductIndex]);
      props.onClose();
      return;
    }
    setIsAdding(true);
    try {
      await props.addItemWithScanner(
        { gtin: props.selectedGtin, cartId },
        props.selectedMrp,
        props.SelectedSellingPrice,
        props.batchNo,
        props.purchasePrice,
        props.sellingUnit,
        props.offerData,
        props.availableUnits,
        props.expiryDate,
        props.brandName,
        props.packagingType,
        props.variantName,
        props.vendorId
      );
    } finally {
      setIsAdding(false);
      props.onClose();
    }
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  return (
    <Modal open={props.open} onClose={handleClose} disableAutoFocus>
      <div
        className={`bp-root${isMobile ? " bp-root--mobile" : ""}${color === "dark" ? " dark" : ""}`}
      >
        <div className="bp-modal">
          {/* ── Header ── */}
          <div className="bp-header">
            <div className="bp-header-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z" />
                <path d="M7 7h.01" />
              </svg>
            </div>
            <div className="bp-header-text">
              <h3 className="bp-title">Select a Batch</h3>
              <p className="bp-subtitle">{props.selectedProduct}</p>
              {props.selectedGtin && <p className="bp-gtin">Barcode: {props.selectedGtin}</p>}
            </div>
            <button
              className="bp-close-btn"
              type="button"
              onClick={handleClose}
              disabled={isAdding}
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* ── Batch count pill ── */}
          <div className="bp-meta-bar">
            <span className="bp-count-pill">
              {batches?.length} batch{batches?.length !== 1 ? "es" : ""} available
            </span>
            <span className="bp-keyboard-hint">↑↓ to navigate · Enter to select</span>
          </div>

          {/* ── Batch list ── */}
          <div className="bp-list" ref={listRef}>
            {batches?.map((batch, i) => {
              const expired = showExpiry && isExpired(batch?.expiryDate);
              const isSelected = selectedProductIndex === i;
              const hasOffers = batch?.offers?.offersList?.length > 0;
              const batchLabel = !batch?.isMultiple
                ? batch?.batchId || batch?.batch || batch?.batchNum || batch?.batchNo || "—"
                : "Multiple batches";

              return (
                <button
                  key={i}
                  type="button"
                  className={`bp-card${isSelected ? " bp-card--selected" : ""}${
                    expired ? " bp-card--expired" : ""
                  }`}
                  disabled={isAdding}
                  onClick={() => handleBatchPopupClick(batch, i)}
                >
                  {/* Custom radio dot */}
                  <div className={`bp-radio${isSelected ? " bp-radio--on" : ""}`}>
                    {isSelected && <div className="bp-radio-dot" />}
                  </div>

                  <div className="bp-card-body">
                    {/* Price row */}
                    <div className="bp-prices">
                      <span className="bp-sp">SP: {getStoreCurrencySymbol()}{batch?.sellingPrice}</span>
                      {batch?.mrp > batch?.sellingPrice && (
                        <span className="bp-mrp">MRP: {getStoreCurrencySymbol()}{batch?.mrp}</span>
                      )}
                      {hasOffers && !expired && (
                        <span className="bp-offer-tag">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M20 12v10H4V12" />
                            <path d="M22 7H2v5h20V7z" />
                            <path d="M12 22V7" />
                            <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                            <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                          </svg>
                          Offer
                        </span>
                      )}
                    </div>

                    {/* Info chips: qty · expiry · batch ID */}
                    <div className="bp-chips">
                      <span className="bp-chip">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2Z" />
                          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                        </svg>
                        {batch?.quantity} units
                      </span>
                      {showExpiry && (
                        <span className={`bp-chip${expired ? " bp-chip--expired" : ""}`}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                          </svg>
                          {batch?.expiryDate && !isNaN(new Date(batch?.expiryDate))
                            ? new Date(batch?.expiryDate).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })
                            : "N/A"}
                        </span>
                      )}
                      <span className="bp-chip bp-chip--batch">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <rect x="2" y="5" width="20" height="14" rx="2" />
                          <line x1="2" y1="10" x2="22" y2="10" />
                        </svg>
                        {batchLabel}
                      </span>
                    </div>

                    {/* Offer names */}
                    {hasOffers && (
                      <div className="bp-offer-names">
                        {batch?.offers?.offersList?.map((o) => (
                          <span key={o?.offerName} className="bp-offer-name-chip">
                            {o?.offerName}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {expired && <span className="bp-expired-pill">EXPIRED</span>}
                </button>
              );
            })}
          </div>

          {/* ── Footer ── */}
          <div className="bp-footer">
            <button
              className="bp-btn bp-btn--cancel"
              type="button"
              onClick={handleClose}
              disabled={isAdding}
            >
              Cancel
            </button>
            <button
              className={`bp-btn bp-btn--add${isAdding ? " bp-btn--loading" : ""}`}
              disabled={
                isAdding || (props.onBatchConfirm ? selectedProductIndex < 0 : props.disabled)
              }
              ref={buttonRef}
              type="button"
              onClick={handleAddClick}
            >
              {isAdding ? (
                <span className="bp-btn-spinner" aria-hidden="true" />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
              )}
              {props.onBatchConfirm ? "Add to Bundle" : isAdding ? "Adding..." : "Add to Cart"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default memo(BatchSelectionModal);
